import { formatDateAsISO } from "core-integration/src/utils/datetime";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useCountdown } from "../../hooks/useCountdown";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import {
  ReducedMotionTime,
  Separator,
  TimeLabel,
  TimerContainer,
  TimeUnit,
} from "./styles";
import { CountdownDisplayProps, CountdownTimerProps, UnitKey } from "./types";
import { formatTime, unitKeys, unitLabel } from "./utils";

const CountdownDisplay = ({
  values,
  timeLeft,
  targetDate,
  ariaLabel,
}: CountdownDisplayProps) => {
  const { t } = useTranslation();

  return (
    <div role="timer" aria-label={ariaLabel}>
      <TimerContainer dateTime={formatDateAsISO(targetDate)}>
        {unitKeys.map((key, index) => {
          const numericValue = timeLeft[key];

          return (
            <Fragment key={key}>
              <TimeUnit>
                <span>{values[key]}</span>
                <TimeLabel>{unitLabel(t, key, numericValue)}</TimeLabel>
              </TimeUnit>
              {index < unitKeys.length - 1 && (
                <Separator aria-hidden="true">:</Separator>
              )}
            </Fragment>
          );
        })}
      </TimerContainer>
    </div>
  );
};

const CountdownTimer = ({
  targetDate,
  deadlineLabel,
  onComplete,
}: CountdownTimerProps) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );

  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const timeLeft = useCountdown(targetDate, { onComplete: handleComplete });

  if (!targetDate || !timeLeft) {
    return null;
  }

  const dateTimeISO = formatDateAsISO(targetDate);
  const isExpired = timeLeft.total <= 0;

  const values: Record<UnitKey, string> = isExpired
    ? {
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      }
    : {
        days: formatTime(timeLeft.days),
        hours: formatTime(timeLeft.hours),
        minutes: formatTime(timeLeft.minutes),
        seconds: formatTime(timeLeft.seconds),
      };

  const ariaLabel = isExpired
    ? t("countdownTimer.expired", "Countdown expired")
    : t("countdownTimer.timeRemaining", {
        defaultValue:
          "Time remaining: {{days}} {{daysLabel}}, {{hours}} {{hoursLabel}}, {{minutes}} {{minutesLabel}}, {{seconds}} {{secondsLabel}}",
        days: timeLeft.days,
        daysLabel: unitLabel(t, "days", timeLeft.days),
        hours: timeLeft.hours,
        hoursLabel: unitLabel(t, "hours", timeLeft.hours),
        minutes: timeLeft.minutes,
        minutesLabel: unitLabel(t, "minutes", timeLeft.minutes),
        seconds: timeLeft.seconds,
        secondsLabel: unitLabel(t, "seconds", timeLeft.seconds),
      });

  const timer = prefersReducedMotion ? (
    <div role="timer" aria-label={ariaLabel}>
      <ReducedMotionTime dateTime={dateTimeISO}>
        {deadlineLabel}
      </ReducedMotionTime>
    </div>
  ) : (
    <CountdownDisplay
      values={values}
      timeLeft={timeLeft}
      targetDate={targetDate}
      ariaLabel={ariaLabel}
    />
  );

  return <>{timer}</>;
};

export default CountdownTimer;
